import { useMemo, useState, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { Link, useLocation, useParams, useSearchParams } from 'react-router-dom';
import styled from 'styled-components';
import { SlidersHorizontal } from 'lucide-react';
import { isApiEnabled } from '../config/env';
import { resolveStorefrontHostHeader } from '../config/storefrontHost';
import { buildShopProductApiParams } from '../utils/buildShopProductApiParams';
import { buildShopBrowsePillsFromCategoryTree } from '../utils/buildShopBrowsePills';
import { getPublicBrowseTagsForCategory } from '../services/browseTagService';
import { productService } from '../services/productService';
import ShopFiltersPanel from '../components/commerce/ShopFiltersPanel';
import {
  ShopActiveFilterChips,
  ShopCatalogPagination,
  ShopFilterTrigger,
} from '../components/commerce/ShopPlpChrome';
import {
  buildShopPlpApiParams,
  countActivePlpFilters,
  parseShopPlpSearchParams,
  patchShopPlpSearchParams,
  plpMatchesPriceBucket,
  priceBucketToRange,
  PRICE_BUCKET_DEFS,
  PLP_PAGE_SIZE,
} from '../utils/shopPlpQuery';
import { modamaiTrace } from '../utils/modamaiTrace';
import { useAppDispatch, useAppSelector } from '../store/hooks';
import { fetchShopCatalog } from '../store/actions/productActions';
import { ProductCard } from '../components/commerce/ProductCard';
import { ProductCardsGrid } from '../components/commerce/ProductCardLayout';
import { Button } from '../components/ui/Button';
import { Container } from '../components/layout/Primitives';
import CategoryHorizontalNav from '../components/layout/CategoryHorizontalNav';
import { siteImagery } from '../config/siteImagery';

const categoryMeta = {
  new: {
    title: 'New In',
    tagline: 'Just Arrived',
    description:
      'The newest pieces from our atelier — fresh tailoring, considered essentials.',
  },
  bestsellers: {
    title: 'Best Sellers',
    tagline: 'Most Loved',
    description: "The pieces our clients can't stop wearing.",
  },
  women: {
    title: 'Women',
    tagline: 'The Womenswear Edit',
    description:
      'Considered tailoring and quietly luxurious essentials, made for her.',
  },
  men: {
    title: 'Men',
    tagline: 'The Menswear Edit',
    description:
      'Sharp tailoring, refined knits and elevated daily essentials, made for him.',
  },
  accessories: {
    title: 'Accessories',
    tagline: 'The Finishing Touch',
    description:
      'Leather goods, footwear and small leather objects — the punctuation to every look.',
  },
  tailoring: {
    title: 'Tailoring',
    tagline: 'Sharp Suiting',
    description: 'Blazers, trousers and suits cut from fluid Italian wool.',
  },
  lounge: {
    title: 'Lounge',
    tagline: 'Cashmere Comfort',
    description: 'Elevated essentials for slow Sundays and long flights.',
  },
  outerwear: {
    title: 'Outerwear',
    tagline: 'Coats & Coverings',
    description: 'Coats designed to outlast every season.',
  },
  knitwear: {
    title: 'Knitwear',
    tagline: 'The Knit Edit',
    description: 'Merino, cashmere and ribbed essentials.',
  },
  dresses: {
    title: 'Dresses',
    tagline: 'After Hours',
    description: 'Slip dresses and evening pieces in fluid silks.',
  },
  sale: {
    title: 'Sale',
    tagline: 'Selected Reductions',
    description: 'Considered pieces, now thoughtfully reduced.',
  },
  all: {
    title: 'All',
    tagline: 'The Maison',
    description: 'Every piece in the collection.',
  },
};

const TH = {
  dresses: siteImagery.look1,
  knitwear: siteImagery.look2,
  outerwear: siteImagery.look3,
  tailoring: siteImagery.catMen,
  lounge: siteImagery.catLounge,
  women: siteImagery.catWomen,
  men: siteImagery.catMen,
  accessories: siteImagery.catAccessories,
  new: siteImagery.heroMain,
};

const BROWSE_PILLS = {
  women: [
    { slug: 'dresses', label: 'Dresses', thumb: TH.dresses },
    { slug: 'knitwear', label: 'Knitwear', thumb: TH.knitwear },
    { slug: 'outerwear', label: 'Outerwear', thumb: TH.outerwear },
    { slug: 'tailoring', label: 'Tailoring', thumb: TH.tailoring },
    { slug: 'lounge', label: 'Lounge', thumb: TH.lounge },
  ],
  men: [
    { slug: 'tailoring', label: 'Tailoring', thumb: TH.tailoring },
    { slug: 'knitwear', label: 'Knitwear', thumb: TH.knitwear },
    { slug: 'outerwear', label: 'Outerwear', thumb: TH.outerwear },
    { slug: 'lounge', label: 'Lounge', thumb: TH.lounge },
  ],
  accessories: [
    { slug: 'women', label: 'Women', thumb: TH.women },
    { slug: 'men', label: 'Men', thumb: TH.men },
    { slug: 'new', label: 'New In', thumb: TH.new },
  ],
  knitwear: [
    { slug: 'women', label: 'Women', thumb: TH.women },
    { slug: 'men', label: 'Men', thumb: TH.men },
    { slug: 'lounge', label: 'Lounge', thumb: TH.lounge },
  ],
  outerwear: [
    { slug: 'women', label: 'Women', thumb: TH.women },
    { slug: 'men', label: 'Men', thumb: TH.men },
    { slug: 'tailoring', label: 'Tailoring', thumb: TH.tailoring },
  ],
  tailoring: [
    { slug: 'women', label: 'Women', thumb: TH.women },
    { slug: 'men', label: 'Men', thumb: TH.men },
    { slug: 'outerwear', label: 'Outerwear', thumb: TH.outerwear },
  ],
  lounge: [
    { slug: 'women', label: 'Women', thumb: TH.women },
    { slug: 'men', label: 'Men', thumb: TH.men },
    { slug: 'knitwear', label: 'Knitwear', thumb: TH.knitwear },
  ],
  new: [
    { slug: 'women', label: 'Women', thumb: TH.women },
    { slug: 'men', label: 'Men', thumb: TH.men },
    { slug: 'accessories', label: 'Accessories', thumb: TH.accessories },
  ],
  bestsellers: [
    { slug: 'women', label: 'Women', thumb: TH.women },
    { slug: 'men', label: 'Men', thumb: TH.men },
    { slug: 'dresses', label: 'Dresses', thumb: TH.dresses },
  ],
  sale: [
    { slug: 'women', label: 'Women', thumb: TH.women },
    { slug: 'men', label: 'Men', thumb: TH.men },
    { slug: 'dresses', label: 'Dresses', thumb: TH.dresses },
  ],
};

/**
 * /shop/dresses rail: facet slugs in `?tag=` — filter `product.facets`, not `tags` (promo).
 */
const DRESSES_NAV_TAGS = [
  { slug: null, label: 'All dresses', thumb: TH.dresses },
  { slug: 'mini', label: 'Mini', thumb: siteImagery.look2 },
  { slug: 'midi', label: 'Midi', thumb: siteImagery.look1 },
  { slug: 'maxi', label: 'Maxi', thumb: siteImagery.look3 },
  { slug: 'formal', label: 'Formal', thumb: siteImagery.catWomen },
  { slug: 'party', label: 'Party', thumb: siteImagery.heroMain },
  { slug: 'lace', label: 'Lace', thumb: TH.dresses },
  { slug: 'day', label: 'Day', thumb: siteImagery.catLounge },
  { slug: 'evening', label: 'Evening', thumb: siteImagery.catResort },
];

const HERO_SPLITS = ['women', 'men', 'accessories'];

/** Toggle when you want the split PLP banner back */
const SHOW_SHOP_CATEGORY_HERO = false;

/** Maison-inspired split banner — ~42vh band on desktop */
const HeroSection = styled.section`
  position: relative;
  display: flex;
  flex-direction: column;
  min-height: 0;

  @media (min-width: ${({ theme }) => theme.breakpoints.md}) {
    flex-direction: row;
    align-items: stretch;
    height: clamp(320px, 42vh, 640px);
    min-height: clamp(320px, 42vh, 640px);
  }
`;

const HeroCopy = styled.div`
  flex: 1;
  display: flex;
  align-items: center;
  justify-content: center;
  padding: clamp(22px, 4vw, 40px) clamp(20px, 3.5vw, 36px);
  background: hsl(345 42% 90%);

  @media (min-width: ${({ theme }) => theme.breakpoints.md}) {
    width: 50%;
    min-width: 0;
  }
`;

const HeroInner = styled.div`
  text-align: center;
  width: 100%;
  max-width: min(28rem, 92vw);
`;

const HeroKicker = styled.p`
  margin: 0 0 12px;
  font-size: ${({ theme }) => theme.type.bodySm};
  font-weight: 500;
  letter-spacing: 0.22em;
  text-transform: uppercase;
  color: hsla(20, 10%, 10%, 0.58);
`;

const HeroTitle = styled.h2`
  margin: 0 0 14px;
  font-family: ${({ theme }) => theme.fonts.display};
  font-size: ${({ theme }) => theme.type.pageDisplay};
  font-weight: 300;
  line-height: 1.06;

  @media (min-width: ${({ theme }) => theme.breakpoints.md}) {
    font-size: clamp(1.85rem, 2.8vw + 1rem, 3.35rem);
  }
`;

const HeroSub = styled.p`
  margin: 0 0 clamp(16px, 2.5vw, 22px);
  font-size: ${({ theme }) => theme.type.body};
  letter-spacing: 0.06em;
  color: hsla(25, 8%, 12%, 0.72);

  @media (min-width: ${({ theme }) => theme.breakpoints.md}) {
    font-size: clamp(13px, 0.2vw + 12px, 16px);
    display: -webkit-box;
    -webkit-line-clamp: 3;
    -webkit-box-orient: vertical;
    overflow: hidden;
  }
`;

const BtnPrimary = styled(Link)`
  display: inline-flex;
  padding: 12px 24px;
  font-size: ${({ theme }) => theme.type.bodySm};
  font-weight: 500;
  letter-spacing: 0.14em;
  text-transform: uppercase;
  border: 1px solid ${({ theme }) => theme.colors.foreground};
  background: ${({ theme }) => theme.colors.foreground};
  color: ${({ theme }) => theme.colors.background};
  transition: opacity 0.2s ease;

  &:hover {
    opacity: 0.92;
  }
`;

const HeroImageCol = styled.div`
  flex: none;
  width: 100%;
  min-height: 0;
  overflow: hidden;

  @media (min-width: ${({ theme }) => theme.breakpoints.md}) {
    flex: 1;
    width: 50%;
  }
`;

const HeroImg = styled.img`
  display: block;
  width: 100%;
  object-fit: cover;

  @media (max-width: 767px) {
    height: clamp(260px, 42vh, 440px);
    max-height: 440px;
  }

  @media (min-width: ${({ theme }) => theme.breakpoints.md}) {
    height: 100%;
    min-height: 0;
  }
`;

const Breadcrumb = styled.nav`
  display: flex;
  align-items: center;
  flex-wrap: wrap;
  gap: 8px;
  padding: clamp(14px, 2vw, 18px) 0;
  font-size: clamp(10px, 0.12vw + 9px, 13px);
  letter-spacing: 0.08em;
  text-transform: uppercase;
  color: ${({ theme }) => theme.colors.mutedForeground};
`;

const CrumbLink = styled(Link)`
  color: inherit;
  &:hover {
    color: ${({ theme }) => theme.colors.foreground};
  }
`;

const PageTitle = styled.h1`
  margin: 0 0 clamp(18px, 2.5vw, 28px);
  font-family: ${({ theme }) => theme.fonts.display};
  font-size: ${({ theme }) => theme.type.pageDisplay};
  font-weight: 300;
  text-transform: uppercase;
  letter-spacing: -0.02em;
  line-height: 1.05;
`;

const SubItem = styled(Link)`
  flex-shrink: 0;
  text-align: center;
  opacity: ${({ $active }) => ($active ? 1 : 0.72)};
  transition: opacity 0.2s ease;
  color: inherit;

  &:hover {
    opacity: 1;
  }
`;

const SubCircle = styled.div`
  width: clamp(52px, 4vw + 36px, 88px);
  height: clamp(52px, 4vw + 36px, 88px);
  border-radius: 999px;
  overflow: hidden;
  margin: 0 auto clamp(6px, 0.8vw, 10px);
  border: 2px solid
    ${({ $active, theme }) =>
      $active ? theme.colors.foreground : 'transparent'};
  transition: border-color 0.2s ease;
`;

const SubThumb = styled.img`
  width: 100%;
  height: 100%;
  object-fit: cover;
`;

const SubPlaceholder = styled.div`
  width: 100%;
  height: 100%;
  background: ${({ theme }) => theme.colors.secondary};
`;

const SubLabel = styled.span`
  font-size: ${({ theme }) => theme.type.navCategory};
  letter-spacing: 0.08em;
  text-transform: uppercase;
  font-weight: 500;
  max-width: 96px;

  @media (min-width: ${({ theme }) => theme.breakpoints.md}) {
    max-width: 112px;
  }
`;

const Toolbar = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  flex-wrap: wrap;
  gap: 16px;
  border-top: 1px solid ${({ theme }) => theme.colors.border};
  border-bottom: 1px solid ${({ theme }) => theme.colors.border};
  padding: 12px 0;
  margin-bottom: clamp(16px, 2.5vw, 28px);
`;

const ToolLeft = styled.div`
  display: flex;
  align-items: center;
  gap: 12px;
  flex-wrap: wrap;
`;

const ToolRight = styled.div`
  display: flex;
  align-items: center;
  gap: 12px;
`;

const ToolMeta = styled.span`
  font-size: ${({ theme }) => theme.type.bodySm};
  color: ${({ theme }) => theme.colors.mutedForeground};
`;

const SortLabel = styled.span`
  display: none;
  font-size: ${({ theme }) => theme.type.bodySm};
  color: ${({ theme }) => theme.colors.mutedForeground};

  @media (min-width: ${({ theme }) => theme.breakpoints.md}) {
    display: inline;
  }
`;

const SortSelect = styled.select`
  font-size: ${({ theme }) => theme.type.bodySm};
  font-family: inherit;
  font-weight: 500;
  border: none;
  background: transparent;
  cursor: pointer;
  outline: none;
  color: ${({ theme }) => theme.colors.foreground};
`;

const ProductGrid = styled(ProductCardsGrid)`
  padding-bottom: clamp(48px, 8vw, 88px);
`;

const Empty = styled.div`
  text-align: center;
  padding: clamp(48px, 10vw, 88px) 0;
`;

const EmptyTitle = styled.h2`
  font-family: ${({ theme }) => theme.fonts.display};
  font-size: ${({ theme }) => theme.type.cardTitle};
  font-weight: 300;
  margin: 0 0 16px;
`;

const EmptyLead = styled.p`
  margin: 0 0 24px;
  color: ${({ theme }) => theme.colors.mutedForeground};
  font-size: ${({ theme }) => theme.type.body};
`;

function browsePillsFor(cat) {
  const rows = BROWSE_PILLS[cat];
  if (!rows) return [];
  return rows.filter((r) => r.slug !== cat);
}

export default function Shop() {
  const { t } = useTranslation();
  const { pathname } = useLocation();
  const [searchParams, setSearchParams] = useSearchParams();
  const { category = 'all', subcategory = '' } = useParams();
  const cat = String(category || 'all').toLowerCase();
  const subcat = String(subcategory || '').toLowerCase();
  const plpSlug = subcat || cat;
  const meta = categoryMeta[plpSlug] ?? categoryMeta[cat] ?? categoryMeta.all;

  const categoryRoots = useAppSelector((s) =>
    Array.isArray(s.category?.roots) ? s.category.roots : [],
  );
  const dispatch = useAppDispatch();
  const apiCatalog = useAppSelector((s) =>
    Array.isArray(s.product?.shopCatalogList) ? s.product.shopCatalogList : [],
  );
  const catalogLoading = useAppSelector((s) => s.product.shopCatalogLoading);
  const catalogError = useAppSelector((s) => s.product.shopCatalogError);
  const productApiExtra = useMemo(
    () => buildShopProductApiParams(plpSlug, categoryRoots),
    [plpSlug, categoryRoots],
  );
  const useApiCatalog = isApiEnabled() && productApiExtra !== undefined;

  const plp = useMemo(() => parseShopPlpSearchParams(searchParams), [searchParams]);
  const activeFacetTag = plp.tag;

  const shopCatalogPagination = useAppSelector((s) => s.product?.shopCatalogPagination);

  const [filtersOpen, setFiltersOpen] = useState(false);
  const [browseTagsFromApi, setBrowseTagsFromApi] = useState([]);
  const [filterOptions, setFilterOptions] = useState({ colors: [], sizes: [] });

  const updatePlp = (patch) => {
    setSearchParams(patchShopPlpSearchParams(searchParams, patch));
  };

  const filterCategoryParam =
    productApiExtra?.category != null ? String(productApiExtra.category) : plpSlug;

  useEffect(() => {
    let cancelled = false;
    if (!isApiEnabled() || !plpSlug) {
      setBrowseTagsFromApi([]);
      return undefined;
    }
    (async () => {
      try {
        const list = await getPublicBrowseTagsForCategory(plpSlug);
        if (!cancelled) setBrowseTagsFromApi(Array.isArray(list) ? list : []);
      } catch (e) {
        modamaiTrace('Shop.jsx browse tags fetch failed', { plpSlug, err: e?.message });
        if (!cancelled) setBrowseTagsFromApi([]);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [plpSlug]);

  useEffect(() => {
    if (!useApiCatalog || !filterCategoryParam) return undefined;
    let cancelled = false;
    (async () => {
      try {
        const opts = await productService.getFilterOptions({ category: filterCategoryParam });
        if (!cancelled) {
          setFilterOptions({
            colors: opts.colors || [],
            sizes: opts.sizes || [],
          });
        }
      } catch {
        if (!cancelled) setFilterOptions({ colors: [], sizes: [] });
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [useApiCatalog, filterCategoryParam]);

  useEffect(() => {
    if (!useApiCatalog) return undefined;
    window.scrollTo({ top: 0, behavior: 'smooth' });
    return undefined;
  }, [plp.page, plpSlug, useApiCatalog]);

  useEffect(() => {
    if (!useApiCatalog) {
      modamaiTrace('Shop.jsx skip fetchShopCatalog', { cat, subcat, useApiCatalog });
      return undefined;
    }
    const req = buildShopPlpApiParams(plp, productApiExtra);
    modamaiTrace('Shop.jsx dispatch fetchShopCatalog', req);
    dispatch(fetchShopCatalog(req));
    return undefined;
  }, [useApiCatalog, dispatch, productApiExtra, plp, cat, subcat]);

  const list = useMemo(() => {
    if (!useApiCatalog) return [];
    let arr = [...apiCatalog];
    if (cat === 'bestsellers') {
      arr = arr.filter((p) => (p.tags || []).includes('bestseller'));
    }
    return arr;
  }, [useApiCatalog, apiCatalog, cat]);

  const activePriceBucketId = useMemo(() => {
    const match = PRICE_BUCKET_DEFS.find((b) => plpMatchesPriceBucket(plp, b.id));
    return match?.id ?? null;
  }, [plp]);

  const activeFilterCount = countActivePlpFilters(plp);

  const filterChips = useMemo(() => {
    /** @type {{ key: string, label: string }[]} */
    const chips = [];
    plp.colors.forEach((c) => chips.push({ key: `color:${c}`, label: c }));
    plp.sizes.forEach((s) => chips.push({ key: `size:${s}`, label: s }));
    if (activePriceBucketId) {
      const def = PRICE_BUCKET_DEFS.find((b) => b.id === activePriceBucketId);
      if (def) chips.push({ key: 'price', label: t(`modamai.shop.${def.labelKey}`) });
    }
    if (plp.sale) chips.push({ key: 'sale', label: t('modamai.shop.onSale') });
    return chips;
  }, [plp, activePriceBucketId, t]);

  const handleRemoveChip = (key) => {
    if (key.startsWith('color:')) {
      const c = key.slice(6);
      updatePlp({ colors: plp.colors.filter((x) => x !== c) });
    } else if (key.startsWith('size:')) {
      const s = key.slice(5);
      updatePlp({ sizes: plp.sizes.filter((x) => x !== s) });
    } else if (key === 'price') {
      updatePlp({ minPrice: undefined, maxPrice: undefined });
    } else if (key === 'sale') {
      updatePlp({ sale: false });
    }
  };

  const clearAllFilters = () => {
    const tag = plp.tag;
    const next = new URLSearchParams();
    if (tag) next.set('tag', tag);
    setSearchParams(next);
  };

  const pagination = shopCatalogPagination;
  const totalResults = pagination?.total ?? list.length;
  const totalPages = pagination?.pages ?? 1;
  const currentPage = pagination?.page ?? plp.page;

  useEffect(() => {
    modamaiTrace('Shop.jsx catalog UI state snapshot', {
      cat,
      useApiCatalog,
      apiCatalogLen: apiCatalog.length,
      catalogLoading,
      catalogError,
      listLen: list.length,
      sort: plp.sort,
      page: plp.page,
    });
  }, [
    apiCatalog.length,
    catalogLoading,
    catalogError,
    useApiCatalog,
    cat,
    subcat,
    plp.sort,
    plp.page,
    list.length,
  ]);

  const heroImg = !SHOW_SHOP_CATEGORY_HERO
    ? null
    : cat === 'women'
      ? siteImagery.catWomen
      : cat === 'men'
        ? siteImagery.catMen
        : cat === 'accessories'
          ? siteImagery.catAccessories
          : null;

  const apiBrowsePills = useMemo(
    () => buildShopBrowsePillsFromCategoryTree(cat, categoryRoots),
    [cat, categoryRoots],
  );
  const staticBrowsePills = browsePillsFor(plpSlug);
  const usingApiSubcategoryRail = isApiEnabled() && apiBrowsePills.length > 0;
  const pills = usingApiSubcategoryRail ? apiBrowsePills : staticBrowsePills;
  const pillsKey = pills.map((p) => p.slug).join('|');
  const showSplitHero = !!heroImg && HERO_SPLITS.includes(cat);

  const facetNavItems = useMemo(() => {
    const allThumb = cat === 'dresses' ? TH.dresses : undefined;
    if (isApiEnabled() && browseTagsFromApi.length > 0) {
      return [
        { slug: null, label: t('modamai.shop.allInCategory', { title: meta.title }), thumb: allThumb },
        ...browseTagsFromApi.map((tagRow) => ({
          slug: tagRow.slug,
          label: tagRow.name,
          thumb: (tagRow.imageUrl && String(tagRow.imageUrl).trim()) || undefined,
        })),
      ];
    }
    if (plpSlug === 'dresses') {
      return DRESSES_NAV_TAGS.map((row, idx) =>
        idx === 0
          ? { ...row, label: t('modamai.shop.allInCategory', { title: meta.title }) }
          : row,
      );
    }
    return [];
  }, [browseTagsFromApi, cat, plpSlug, meta.title, t]);

  const facetLabel =
    activeFacetTag && facetNavItems.find((item) => item.slug === activeFacetTag)?.label;
  const plpHeading =
    activeFacetTag && facetLabel ? `${meta.title} · ${facetLabel}` : meta.title;

  return (
    <div className="animate-fade-in">
      {showSplitHero && (
        <HeroSection aria-label={t('modamai.shop.highlightAria', { title: meta.title })}>
          <HeroCopy>
            <HeroInner>
              <HeroKicker>{meta.tagline}</HeroKicker>
              <HeroTitle>{meta.title}</HeroTitle>
              <HeroSub>{meta.description}</HeroSub>
              <BtnPrimary to={`/shop/${cat}`}>{t('modamai.shop.shopCategoryCta', { title: meta.title })}</BtnPrimary>
            </HeroInner>
          </HeroCopy>
          <HeroImageCol>
            <HeroImg src={heroImg} alt="" loading="eager" />
          </HeroImageCol>
        </HeroSection>
      )}

      <Container>
        <Breadcrumb aria-label={t('modamai.shop.breadcrumbAria')}>
          <CrumbLink to="/">{t('modamai.shop.home')}</CrumbLink>
          <span aria-hidden>/</span>
          <span style={{ color: 'inherit' }}>{meta.title}</span>
        </Breadcrumb>

        <PageTitle>{plpHeading}</PageTitle>

        {facetNavItems.length > 0 && (
          <CategoryHorizontalNav
            ariaLabel={t('modamai.shop.browseAria', { title: meta.title })}
            contentKey={`facet|${cat}|${browseTagsFromApi.map((tag) => tag.slug).join('|')}|${activeFacetTag ?? ''}`}
          >
            {facetNavItems.map((facet) => {
              const to = facet.slug
                ? `/shop/${encodeURIComponent(cat)}?tag=${encodeURIComponent(facet.slug)}`
                : `/shop/${encodeURIComponent(cat)}`;
              const active =
                (facet.slug == null && activeFacetTag == null) ||
                (facet.slug != null && activeFacetTag === facet.slug);
              return (
                <SubItem key={facet.slug ?? 'all'} to={to} $active={active}>
                  <SubCircle $active={active}>
                    {facet.thumb ? (
                      <SubThumb src={facet.thumb} alt="" loading="lazy" />
                    ) : (
                      <SubPlaceholder />
                    )}
                  </SubCircle>
                  <SubLabel>{facet.label}</SubLabel>
                </SubItem>
              );
            })}
          </CategoryHorizontalNav>
        )}

        {pills.length ? (
          <CategoryHorizontalNav
            ariaLabel={t('modamai.shop.relatedAria')}
            contentKey={`${cat}|${pillsKey}`}
          >
            {pills.map((p) => {
              const path = usingApiSubcategoryRail
                ? `/shop/${encodeURIComponent(cat)}/${encodeURIComponent(p.slug)}`
                : `/shop/${encodeURIComponent(p.slug)}`;
              const active = usingApiSubcategoryRail
                ? subcat === String(p.slug || '').toLowerCase()
                : pathname === path;
              return (
                <SubItem key={p.slug} to={path} $active={active}>
                  <SubCircle $active={active}>
                    {p.thumb ? (
                      <SubThumb src={p.thumb} alt="" loading="lazy" />
                    ) : (
                      <SubPlaceholder />
                    )}
                  </SubCircle>
                  <SubLabel>{p.label}</SubLabel>
                </SubItem>
              );
            })}
          </CategoryHorizontalNav>
        ) : null}

        <ShopActiveFilterChips
          chips={filterChips}
          onRemove={handleRemoveChip}
          clearAllLabel={t('modamai.shop.clearAll')}
          onClearAll={filterChips.length ? clearAllFilters : null}
        />

        <Toolbar>
          <ToolLeft>
            <ShopFilterTrigger
              activeCount={activeFilterCount}
              onClick={() => setFiltersOpen(true)}
              label={t('modamai.shop.filters')}
              icon={SlidersHorizontal}
            />
          </ToolLeft>
          <ToolRight>
            <ToolMeta>{t('modamai.shop.results', { count: totalResults })}</ToolMeta>
            <SortLabel>{t('modamai.shop.sortBy')}</SortLabel>
            <SortSelect
              value={plp.sort}
              onChange={(e) => updatePlp({ sort: e.target.value })}
            >
              <option value="recommended">{t('modamai.shop.sortRecommended')}</option>
              <option value="newest">{t('modamai.shop.sortNewest')}</option>
              <option value="price-low">{t('modamai.shop.sortPriceLow')}</option>
              <option value="price-high">{t('modamai.shop.sortPriceHigh')}</option>
            </SortSelect>
          </ToolRight>
        </Toolbar>

        {!isApiEnabled() ? (
          <Empty>
            <EmptyTitle>{t('modamai.shop.liveCatalogDisabled')}</EmptyTitle>
            <EmptyLead style={{ opacity: 0.9 }}>{t('modamai.shop.liveCatalogDisabledLead')}</EmptyLead>
          </Empty>
        ) : catalogError && useApiCatalog ? (
          <Empty>
            <EmptyTitle>{t('modamai.shop.catalogUnavailable')}</EmptyTitle>
            <EmptyLead style={{ opacity: 0.85 }}>
              {t('modamai.shop.catalogUnavailableLead', {
                message: String(catalogError),
                host: resolveStorefrontHostHeader(),
              })}
            </EmptyLead>
            <Button
              type="button"
              $variant="outlineLuxe"
              onClick={() => dispatch(fetchShopCatalog(buildShopPlpApiParams(plp, productApiExtra)))}
            >
              {t('modamai.shop.retry')}
            </Button>
          </Empty>
        ) : useApiCatalog && catalogLoading && list.length === 0 ? (
          <Empty aria-busy aria-live="polite">
            <EmptyTitle>{t('modamai.shop.loadingCatalog')}</EmptyTitle>
            <EmptyLead>{t('modamai.shop.loadingCatalogLead')}</EmptyLead>
          </Empty>
        ) : list.length === 0 ? (
          <Empty>
            <EmptyTitle>{t('modamai.shop.noProducts')}</EmptyTitle>
            <EmptyLead>{t('modamai.shop.noProductsLead')}</EmptyLead>
            <Button type="button" $variant="outlineLuxe" onClick={clearAllFilters}>
              {t('modamai.shop.clearFilters')}
            </Button>
          </Empty>
        ) : (
          <>
            <ProductGrid>
              {list.map((p) => (
                <ProductCard key={p.slug || p.id} product={p} />
              ))}
            </ProductGrid>
            <ShopCatalogPagination
              page={currentPage}
              pages={totalPages}
              total={totalResults}
              limit={PLP_PAGE_SIZE}
              disabled={catalogLoading}
              onPageChange={(p) => updatePlp({ page: p, resetPage: false })}
              summaryLabel={({ from, to, total: tot }) =>
                t('modamai.shop.paginationSummary', { from, to, total: tot })
              }
              previousLabel={t('modamai.shop.paginationPrev')}
              nextLabel={t('modamai.shop.paginationNext')}
            />
          </>
        )}
      </Container>

      <ShopFiltersPanel
        open={filtersOpen}
        onClose={() => setFiltersOpen(false)}
        titleLabel={t('modamai.shop.filter')}
        closeAria={t('modamai.shop.closeFilters')}
        saleLabel={t('modamai.shop.onSale')}
        saleOn={plp.sale}
        onSaleChange={(v) => updatePlp({ sale: v })}
        showSaleToggle={cat !== 'sale'}
        priceSectionLabel={t('modamai.shop.priceRange')}
        tPriceLabel={(key) => t(`modamai.shop.${key}`)}
        activePriceBucketId={activePriceBucketId}
        onPriceBucketChange={(id) => {
          if (!id) updatePlp({ minPrice: undefined, maxPrice: undefined });
          else updatePlp(priceBucketToRange(id));
        }}
        colorSectionLabel={t('modamai.shop.color')}
        colorOptions={filterOptions.colors}
        selectedColors={plp.colors}
        onColorsChange={(c) => updatePlp({ colors: c })}
        sizeSectionLabel={t('modamai.shop.size')}
        sizeOptions={filterOptions.sizes}
        selectedSizes={plp.sizes}
        onSizesChange={(s) => updatePlp({ sizes: s })}
        applyLabel={t('modamai.shop.viewPieces', { count: totalResults })}
        clearLabel={t('modamai.shop.clearAll')}
        onClear={clearAllFilters}
      />
    </div>
  );
}
